import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/use-auth'
import { type NotificationChannelHealth } from '@/lib/api'
import { apiV2 } from '@/lib/api-v2-client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ChevronLeft, ChevronRight } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/admin_/notifications')({
  component: AdminNotificationsPage,
})

const PER_PAGE = 50

const CHANNEL_TYPES = ['email', 'slack', 'discord', 'telegram', 'webhook', 'teams', 'sms', 'phone'] as const
const STATUSES = ['delivered', 'failed', 'skipped'] as const

export function AdminNotificationsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [channelFilter, setChannelFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [page, setPage] = useState(1)

  const { data: health, isLoading: healthLoading } = useQuery({
    queryKey: ['admin-notification-health'],
    queryFn: () => apiV2.adminGetNotificationHealth(),
    refetchInterval: 30_000,
    enabled: !!user?.is_admin,
  })

  const { data: deliveriesData } = useQuery({
    queryKey: ['admin-notification-deliveries', channelFilter, statusFilter, page],
    queryFn: () =>
      apiV2.adminListNotificationDeliveries({
        channel_type: channelFilter === 'all' ? undefined : channelFilter,
        status: statusFilter === 'all' ? undefined : statusFilter,
        page,
        per_page: PER_PAGE,
      }),
    enabled: !!user?.is_admin,
  })

  const deliveries = deliveriesData?.data ?? []
  const totalDeliveries = deliveriesData?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(totalDeliveries / PER_PAGE))

  const channelRows = useMemo(() => {
    if (!health?.channels) return []
    return Object.entries(health.channels)
      .map(([type, data]) => ({ type, ...data }))
      .sort((a, b) => (b.delivered_7d + b.failed_7d) - (a.delivered_7d + a.failed_7d))
  }, [health])

  // Sorted worst-first: the channel that has been dead longest is the one
  // most likely to have silently missed a real outage.
  const quarantined = useMemo(
    () =>
      [...(health?.disabled_channels ?? [])].sort(
        (a, b) => b.consecutive_failures - a.consecutive_failures,
      ),
    [health],
  )

  const topOrgFailures = useMemo(() => {
    if (!health?.per_org) return []
    return [...health.per_org].sort((a, b) => b.failed - a.failed).slice(0, 10)
  }, [health])

  if (!user?.is_admin) {
    navigate({ to: '/dashboard' })
    return null
  }

  return (
    <div className="space-y-6 pb-20">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Notification Health</h1>
          <p className="text-sm text-muted-foreground">Delivery health across all orgs · last 7 days</p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/admin">Admin · Users</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Per-channel summary</CardTitle>
          <CardDescription>Delivered/failed counts and latency over the last 7 days</CardDescription>
        </CardHeader>
        <CardContent>
          {healthLoading ? (
            <div className="h-24 animate-pulse rounded bg-muted" />
          ) : channelRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No notification data in the last 7 days.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Channel</TableHead>
                  <TableHead className="text-right">Delivered</TableHead>
                  <TableHead className="text-right">Failed</TableHead>
                  <TableHead className="text-right">Skipped</TableHead>
                  <TableHead className="text-right">Fail rate</TableHead>
                  <TableHead className="text-right">p95 latency</TableHead>
                  <TableHead>Last success</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {channelRows.map(({ type, ...rest }) => (
                  <ChannelHealthRow key={type} type={type} {...rest} />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {quarantined.length > 0 && (
        <Card className="border-status-degraded">
          <CardHeader>
            <CardTitle>Auto-disabled channels</CardTitle>
            <CardDescription>
              Uptrack stopped sending to these after repeated unrecoverable failures — a revoked
              token or a deleted webhook. Their owners were emailed. Until each is fixed and
              re-enabled, those orgs receive no alerts on that channel.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Channel</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Why</TableHead>
                  <TableHead className="text-right">Failures</TableHead>
                  <TableHead>Disabled</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quarantined.map(row => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell>{row.channel_type}</TableCell>
                    <TableCell
                      className="max-w-xl text-xs text-muted-foreground"
                      title={row.disabled_reason ?? undefined}
                    >
                      {row.disabled_reason ?? '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="destructive">{row.consecutive_failures}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.disabled_at ? new Date(row.disabled_at).toLocaleString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {topOrgFailures.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top orgs by failed deliveries</CardTitle>
            <CardDescription>Last 7 days</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead className="text-right">Delivered</TableHead>
                  <TableHead className="text-right">Failed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topOrgFailures.map(row => (
                  <TableRow key={row.org_id}>
                    <TableCell className="font-mono text-xs">{row.org_name || row.org_id}</TableCell>
                    <TableCell className="text-right">{row.delivered}</TableCell>
                    <TableCell className="text-right">
                      {row.failed > 0 ? <Badge variant="destructive">{row.failed}</Badge> : row.failed}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {health?.error_breakdown && health.error_breakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Error breakdown</CardTitle>
            <CardDescription>Most frequent failure messages</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Channel</TableHead>
                  <TableHead>Error</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {health.error_breakdown.slice(0, 15).map((row, i) => (
                  <TableRow key={`${row.channel_type}-${i}`}>
                    <TableCell>{row.channel_type}</TableCell>
                    <TableCell className="max-w-xl truncate text-xs text-muted-foreground" title={row.error_message}>
                      {row.error_message}
                    </TableCell>
                    <TableCell className="text-right">{row.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Recent deliveries</CardTitle>
          <CardDescription>Most recent {PER_PAGE} per page · filter to investigate</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Select value={channelFilter} onValueChange={(v) => { setChannelFilter(v); setPage(1) }}>
              <SelectTrigger className="w-44" aria-label="Filter by channel">
                <SelectValue placeholder="All channels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All channels</SelectItem>
                {CHANNEL_TYPES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1) }}>
              <SelectTrigger className="w-44" aria-label="Filter by status">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">{totalDeliveries.toLocaleString()} total</span>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Org</TableHead>
                <TableHead>Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deliveries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                    No deliveries match the current filters.
                  </TableCell>
                </TableRow>
              ) : (
                deliveries.map(d => (
                  <TableRow key={d.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {new Date(d.inserted_at).toLocaleString()}
                    </TableCell>
                    <TableCell>{d.channel_type}</TableCell>
                    <TableCell className="text-xs">{d.event_type}</TableCell>
                    <TableCell>
                      <StatusBadge status={d.status} />
                    </TableCell>
                    <TableCell className="text-xs">{d.organization_name}</TableCell>
                    <TableCell className="max-w-md truncate text-xs text-muted-foreground" title={d.error_message ?? ''}>
                      {d.error_message ?? '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="h-4 w-4" /> Prev
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function ChannelHealthRow({ type, ...data }: { type: string } & NotificationChannelHealth) {
  const failPct = (data.fail_rate_7d * 100).toFixed(1)
  const failBadge = data.fail_rate_7d >= 0.1
    ? <Badge variant="destructive">{failPct}%</Badge>
    : data.fail_rate_7d >= 0.01
      ? <Badge variant="secondary">{failPct}%</Badge>
      : <span className="text-muted-foreground">{failPct}%</span>

  return (
    <TableRow>
      <TableCell className="font-medium">{type}</TableCell>
      <TableCell className="text-right">{data.delivered_7d.toLocaleString()}</TableCell>
      <TableCell className="text-right">{data.failed_7d.toLocaleString()}</TableCell>
      <TableCell className="text-right text-muted-foreground">{data.skipped_7d.toLocaleString()}</TableCell>
      <TableCell className="text-right">{failBadge}</TableCell>
      <TableCell className="text-right num text-xs">{data.p95_duration_ms ? `${Math.round(data.p95_duration_ms)}ms` : '—'}</TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {data.last_success_at ? new Date(data.last_success_at).toLocaleString() : '—'}
      </TableCell>
    </TableRow>
  )
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'delivered') return <Badge variant="default" className="bg-status-up-soft text-status-up">delivered</Badge>
  if (status === 'failed') return <Badge variant="destructive">failed</Badge>
  return <Badge variant="secondary">{status}</Badge>
}
